import { Drawer, Table } from '@/components/commons';
import { ENDPOINT } from '@/services/endpoint';
import { FALLBACK_URL } from '@/services/image';
import { ScreenMediaService } from '@/services/screenMedia';
import { IResponse, IResponseError } from '@/types/common';
import {
  ICreateScreenMediaRequest,
  IUpdateScreenMediaRequest,
} from '@/types/request/screenMedia';
import {
  IScreenMedia,
  SCREEN_MEDIA_PLACEMENT_LABELS,
  SCREEN_MEDIA_STATUS_LABELS,
  SCREEN_MEDIA_TYPE_LABELS,
  ScreenMediaPlacement,
  ScreenMediaStatus,
  ScreenMediaType,
} from '@/types/response/screenMedia';
import {
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Tabs, Tag, TableColumnsType } from 'antd';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { ScreenMediaForm } from '../Drawers/ScreenMediaForm';
import { formSchemaFn } from '../Drawers/ScreenMediaForm/formSchemaFn';
import { RotationPreview } from './RotationPreview';

interface IProps {
  title?: string;
}

type FormValues = {
  title: string;
  mediaType: ScreenMediaType;
  mediaUrl: string;
  posterUrl: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  fileSizeBytes: number | null;
  videoCodec: string | null;
  sortOrder: number;
  publicAt?: Moment | null;
  publicEndAt?: Moment | null;
  status: ScreenMediaStatus;
};

const defaultValues: FormValues = {
  title: '',
  mediaType: ScreenMediaType.VIDEO,
  mediaUrl: '',
  posterUrl: null,
  durationSec: null,
  width: null,
  height: null,
  fileSizeBytes: null,
  videoCodec: null,
  sortOrder: 0,
  publicAt: null,
  publicEndAt: null,
  status: ScreenMediaStatus.DRAFT,
};

/** 現状は表示面が 1 つだけ。増えたらここをセレクトにする */
const PLACEMENT = ScreenMediaPlacement.WAITING_CREATE_POST;

const statusColors: Record<ScreenMediaStatus, string> = {
  [ScreenMediaStatus.DRAFT]: 'default',
  [ScreenMediaStatus.PUBLIC]: 'green',
  [ScreenMediaStatus.HIDDEN]: 'orange',
};

/**
 * 日記の作成中画面に差し込む動画・静止画の管理。
 * 公開期間中のものが 3 日ごとに切り替わり、全ユーザーが同じ日に同じ 1 件を見る。
 */
export const ScreenMediaList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IScreenMedia>();

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.SCREEN_MEDIA.GET_LIST, PLACEMENT],
    () => ScreenMediaService.getList({ placement: PLACEMENT, limit: 200 }),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn()),
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset(defaultValues);
  };

  const { mutate: createMedia, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateScreenMediaRequest) => ScreenMediaService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'メディアを登録しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => handleErrorCommon(error, setError),
    },
  );

  const { mutate: updateMedia, isLoading: isLoadingUpdate } = useMutation(
    ({ body, id }: { body: IUpdateScreenMediaRequest; id: number }) =>
      ScreenMediaService.update(body, id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'メディアを更新しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => handleErrorCommon(error, setError),
    },
  );

  const { mutate: deleteMedia, isLoading: isLoadingDelete } = useMutation(
    (id: number) => ScreenMediaService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'メディアを削除しました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => handleErrorCommon(error, setError),
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.SCREEN_MEDIA.GET_DETAIL.replace(':id', String(currentId))],
    () => ScreenMediaService.getDetail(currentId as number),
    {
      enabled: !!currentId,
      onSuccess: (res: IResponse<IScreenMedia>) => {
        setDetail(res?.data);
        reset({
          title: res?.data?.title ?? '',
          mediaType: res?.data?.mediaType ?? ScreenMediaType.VIDEO,
          mediaUrl: res?.data?.mediaUrl ?? '',
          posterUrl: res?.data?.posterUrl ?? null,
          // numeric / bigint は文字列で返るのでフォーム側で数値に戻す
          durationSec:
            res?.data?.durationSec != null
              ? Number(res.data.durationSec)
              : null,
          width: res?.data?.width ?? null,
          height: res?.data?.height ?? null,
          fileSizeBytes:
            res?.data?.fileSizeBytes != null
              ? Number(res.data.fileSizeBytes)
              : null,
          videoCodec: res?.data?.videoCodec ?? null,
          sortOrder: res?.data?.sortOrder ?? 0,
          publicAt: res?.data?.publicAt ? moment(res.data.publicAt) : null,
          publicEndAt: res?.data?.publicEndAt
            ? moment(res.data.publicEndAt)
            : null,
          status: res?.data?.status ?? ScreenMediaStatus.DRAFT,
        });
      },
    },
  );

  const onSubmit = (values: FormValues) => {
    // publicEndAt は null を送ることで「無期限に戻す」意味になる。
    // undefined だとサーバー側では「触らない」扱いになり、無言で無視される。
    const payload = {
      title: values.title,
      mediaType: values.mediaType,
      mediaUrl: values.mediaUrl,
      posterUrl: values.posterUrl ?? null,
      durationSec: values.durationSec ?? null,
      width: values.width ?? null,
      height: values.height ?? null,
      fileSizeBytes: values.fileSizeBytes ?? null,
      videoCodec: values.videoCodec ?? null,
      sortOrder: values.sortOrder,
      publicAt: values.publicAt ? values.publicAt.toISOString() : null,
      publicEndAt: values.publicEndAt ? values.publicEndAt.toISOString() : null,
      status: values.status,
    };

    if (currentId) {
      updateMedia({ body: payload, id: currentId });

      return;
    }

    createMedia({ ...payload, placement: PLACEMENT });
  };

  const handleDelete = () => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteMedia(detail!.id),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const columns: TableColumnsType<IScreenMedia> = [
    {
      title: 'プレビュー',
      dataIndex: 'posterUrl',
      align: 'center',
      width: 90,
      render: (value, record) => {
        const thumb = value || record.mediaUrl;

        return record.mediaType === ScreenMediaType.IMAGE || value ? (
          <Image
            width={56}
            height={70}
            src={thumb ?? ''}
            preview={false}
            fallback={FALLBACK_URL}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span className='text-gray-400'>なし</span>
        );
      },
    },
    { title: '管理名', dataIndex: 'title', align: 'left' },
    {
      title: '種別',
      dataIndex: 'mediaType',
      align: 'center',
      width: 90,
      render: (value: ScreenMediaType) => SCREEN_MEDIA_TYPE_LABELS[value],
    },
    {
      title: '尺',
      dataIndex: 'durationSec',
      align: 'center',
      width: 80,
      render: (value) =>
        value != null ? `${Math.round(Number(value))}秒` : '―',
    },
    {
      title: '公開期間',
      dataIndex: 'publicAt',
      align: 'center',
      width: 220,
      render: (value, record) => (
        <div className='text-xs'>
          <div>
            {value ? moment(value).format('YYYY/MM/DD HH:mm') : '未設定'}
          </div>
          <div className='text-gray-500'>
            〜
            {record.publicEndAt
              ? moment(record.publicEndAt).format('YYYY/MM/DD HH:mm')
              : '無期限'}
          </div>
        </div>
      ),
    },
    {
      title: '状態',
      dataIndex: 'status',
      align: 'center',
      width: 100,
      render: (value: ScreenMediaStatus) => (
        <Tag color={statusColors[value]}>
          {SCREEN_MEDIA_STATUS_LABELS[value]}
        </Tag>
      ),
    },
    {
      title: 'ローテーション順',
      dataIndex: 'sortOrder',
      align: 'center',
      width: 130,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <div className='flex justify-between items-center mb-4'>
        <div className='text-sm text-gray-500'>
          {SCREEN_MEDIA_PLACEMENT_LABELS[PLACEMENT]}
          に表示するメディア。公開中のものが
          <span className='font-semibold text-black'>3日ごと</span>
          に切り替わり、全ユーザーが同じ日に同じ1件を見ます。
        </div>
        <Button
          type='primary'
          onClick={() => {
            reset(defaultValues);
            setOpenDrawer(true);
          }}
        >
          新規登録
        </Button>
      </div>

      <Tabs
        items={[
          {
            key: 'list',
            label: '一覧',
            children: (
              <Table
                columns={columns}
                dataSource={data?.data?.data ?? []}
                rowKey='id'
                loading={isFetching}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    setCurrentId(record.id);
                    setOpenDrawer(true);
                  },
                })}
              />
            ),
          },
          {
            key: 'rotation',
            label: 'ローテーション',
            children: <RotationPreview placement={PLACEMENT} />,
          },
        ]}
      />

      <Drawer
        title={currentId ? 'メディアを編集' : 'メディアを登録'}
        open={openDrawer}
        okText={!currentId ? '保存' : '更新'}
        onOk={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onDelete={handleDelete}
        isDeleted={!!currentId}
        loading={isLoadingCreate || isLoadingUpdate || isLoadingDelete}
        maskClosable={false}
      >
        <FormProvider {...methods}>
          <ScreenMediaForm isLoading={isFetchingDetail} />
        </FormProvider>
      </Drawer>
    </>
  );
};
